import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText } from "lucide-react";

const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate("/")}
            >
              <img src={BRAIN_LOGO} alt="ABA Prep Lab" className="w-10 h-10 object-contain" />
              <span className="font-bold text-xl text-slate-900 dark:text-white">ABA Prep Lab</span>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-teal-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 md:p-12"
        >
          {/* Title */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200 dark:border-slate-700">
            <div className="w-14 h-14 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <FileText className="w-7 h-7 text-violet-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Terms of Service</h1>
              <p className="text-slate-500 dark:text-slate-400">Last Updated: January 2026</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
              These Terms of Service ("Terms") govern your access to and use of ABA Prep Lab (the "Site"). By accessing or using the Site, you agree to be bound by these Terms.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">1. Use of the Site</h2>
              <p className="text-slate-700 dark:text-slate-300">
                ABA Prep Lab provides educational materials for BCBA exam preparation purposes only. We do not guarantee exam results or certification outcomes.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">2. Eligibility</h2>
              <p className="text-slate-700 dark:text-slate-300">
                You must be at least 18 years old to use ABA Prep Lab.
              </p>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">3. Accounts</h2>
              <p className="text-slate-700 dark:text-slate-300">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">4. Payments and Subscriptions</h2>
              <p className="text-slate-700 dark:text-slate-300">
                Paid features, if offered, are billed in advance. Fees are non-refundable unless otherwise stated. Subscription terms will be disclosed at purchase.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">5. Intellectual Property</h2>
              <p className="text-slate-700 dark:text-slate-300">
                All content on ABA Prep Lab, including text, quizzes, questions, graphics, and software, is owned by or licensed to us and protected by intellectual property laws. You may not copy, distribute, or reproduce content without permission.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">6. Prohibited Conduct</h2>
              <p className="text-slate-700 dark:text-slate-300 mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 text-slate-700 dark:text-slate-300 space-y-2">
                <li>Use ABA Prep Lab for unlawful purposes</li>
                <li>Attempt to gain unauthorized access to systems</li>
                <li>Copy, resell, or redistribute Site content</li>
                <li>Interfere with Site functionality or security</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">7. Disclaimer</h2>
              <p className="text-slate-700 dark:text-slate-300">
                ABA Prep Lab is provided "as is" and "as available." We make no warranties regarding accuracy, completeness, or fitness for a particular purpose.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-slate-700 dark:text-slate-300">
                To the fullest extent permitted by law, we are not liable for any indirect, incidental, or consequential damages arising from use of ABA Prep Lab.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">9. Termination</h2>
              <p className="text-slate-700 dark:text-slate-300">
                We reserve the right to suspend or terminate access to ABA Prep Lab at our discretion, without notice, for violations of these Terms.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">10. Governing Law</h2>
              <p className="text-slate-700 dark:text-slate-300">
                These Terms are governed by and construed in accordance with the laws of the applicable jurisdiction.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">11. Changes to These Terms</h2>
              <p className="text-slate-700 dark:text-slate-300">
                We may update these Terms from time to time. Continued use of ABA Prep Lab after changes constitutes acceptance of the revised Terms.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">12. Contact Information</h2>
              <p className="text-slate-700 dark:text-slate-300">
                For questions regarding these Terms, please contact us through ABA Prep Lab.
              </p>
            </section>

            {/* Disclaimer */}
            <section className="mt-10 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-300 italic">
                <strong>Disclaimer:</strong> This Privacy Policy and Terms of Service are provided for general informational purposes and do not constitute legal advice.
              </p>
            </section>
          </div>

          {/* Back Button */}
          <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Button
              onClick={() => navigate("/")}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
        <div className="container mx-auto text-center text-slate-500 dark:text-slate-400 text-sm">
          © 2026 ABA Prep Lab. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
